/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

// FIXME: A bit hacky approach
// It is better to access properties via context instead of passing form every time
// If that stops working, you can always pass `form` directly via props or
// use hack with `shouldUpdate` like this
//    <Form.Item shouldUpdate={() => {}}>
//      {(formInstance) => {...}}
//    <Form.Item>
import FieldContext from 'rc-field-form/es/FieldContext';

import { Input } from '@atoms';
import { Form } from '@organisms';

const SOURCE = {
  URL: 'url',
  ENTITY: 'entity',
};

const EntityOrUrlField = React.forwardRef(
  ({ className, placeholder, disabled, style, entityName, urlName = 'url', rules = [], children, ...props }, ref) => {
    const urlInputRef = React.useRef(null);
    const entityInputRef = React.useRef(null);
    const [source, setSource] = React.useState(SOURCE.ENTITY);

    const isSourceUrl = source === SOURCE.URL;
    const isSourceEntity = source === SOURCE.ENTITY;

    const { getFieldValue, setFields, prefixName = [] } = React.useContext(FieldContext);

    React.useEffect(() => {
      const entity = getFieldValue([...prefixName, entityName]);
      const url = getFieldValue([...prefixName, urlName]);

      if (isSourceUrl && !url && entity) {
        setSource(SOURCE.ENTITY);
      } else if (isSourceEntity && !entity && url) {
        setSource(SOURCE.URL);
      }
    }, [getFieldValue, prefixName, entityName, urlName, source, isSourceUrl, isSourceEntity]);
    // FIXME: Unluckily Ant does not expose `onReset` property on from item
    // https://github.com/ant-design/ant-design/blob/master/components/form/FormItem.tsx#L247
    // so expose this method to manually switch into `page` source
    // Let me know if you have a better idea
    const reset = React.useCallback(() => {
      setSource(SOURCE.ENTITY);
      setFields([
        {
          name: [...prefixName, entityName],
          value: undefined,
          errors: [],
        },
        {
          name: [...prefixName, urlName],
          value: undefined,
          errors: [],
        },
      ]);
    }, [entityName, prefixName, setFields, urlName]);

    React.useImperativeHandle(ref, () => ({
      reset,
    }));

    const handleSearch = React.useCallback(
      (value) => {
        if (value.startsWith('http') && isSourceEntity) {
          setSource(SOURCE.URL);
          setFields([
            {
              name: [...prefixName, urlName],
              value,
            },
            {
              name: [...prefixName, entityName],
              value: undefined,
            },
          ]);
        }
      },
      [isSourceEntity, setFields, prefixName, urlName, entityName],
    );

    const handleUrlChange = React.useCallback(
      ({ target: { value } }) => {
        if (isSourceUrl && !value) {
          setSource(SOURCE.ENTITY);
          // eslint-disable-next-line no-unused-expressions
          entityInputRef.current?.focus();
        }
      },
      [isSourceUrl],
    );

    React.useEffect(() => {
      if (isSourceUrl) {
        // eslint-disable-next-line no-unused-expressions
        urlInputRef.current?.focus();
      }
    }, [isSourceUrl, source]);

    React.useEffect(() => {
      if (disabled) {
        reset();
      }
    }, [disabled, reset]);

    const commonProps = {
      className,
      style,
    };

    const urlRules = !disabled && isSourceUrl && [...rules, { type: 'url', message: "Doesn't look like a valid URL" }];
    const entityRules = !disabled && isSourceEntity && rules;

    const disabledEntityStyle = !isSourceEntity ? { overflow: 'hidden', height: 0, margin: 0 } : null;

    return (
      <>
        {isSourceUrl && (
          <Form.Item {...props} {...commonProps} name={urlName} rules={urlRules}>
            <Input
              ref={urlInputRef}
              allowClear
              placeholder={placeholder}
              onChange={handleUrlChange}
              disabled={disabled}
            />
          </Form.Item>
        )}
        {children({
          ...props,
          ...commonProps,
          style: {
            ...commonProps.style,
            ...disabledEntityStyle,
          },
          disabled,
          innerRef: entityInputRef,
          onSearch: handleSearch,
          placeholder,
          rules: entityRules,
          name: entityName,
          showAction: 'focus',
        })}
      </>
    );
  },
);

export default EntityOrUrlField;
